import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { MesaRecipeResult } from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { platformAdminApi } from '../api/platformAdminApi';
import { useActiveTenants } from './recipe.hooks';
import { ClientFields, Question, RecipeDone, RecipeFooter, RecipePage } from './recipeParts';
import { defaultSupportName, notifyCandidates, type NotifyCandidate } from './mesaRecipe.utils';

export function MesaRecipePage() {
  const [error, setError] = useState('');
  const tenants = useActiveTenants(setError);
  const [tenantId, setTenantId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [agentName, setAgentName] = useState('');
  const [scope, setScope] = useState('');
  const [candidates, setCandidates] = useState<NotifyCandidate[] | null>(null);
  const [notify, setNotify] = useState<number[]>([]);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState<MesaRecipeResult | null>(null);

  const loadCandidates = (id: string) => {
    setCandidates(null);
    setNotify([]);
    void platformAdminApi
      .listUsers(id)
      .then(users => {
        const list = notifyCandidates(users);
        setCandidates(list);
        setNotify(list.filter(item => item.canSeeNewBot).map(item => item.id));
      })
      .catch(err => setError(err instanceof Error ? err.message : 'No se pudo cargar el equipo del cliente'));
  };

  const eligible = (candidates ?? []).filter(item => item.canSeeNewBot);
  const answered = [
    Boolean(tenantId && companyName.trim()),
    Boolean(scope.trim()),
    candidates !== null && (notify.length > 0 || eligible.length === 0),
  ];
  const ready = answered.every(Boolean);

  const toggle = (id: number) =>
    setNotify(current => (current.includes(id) ? current.filter(item => item !== id) : [...current, id]));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready || progress) return;
    setError('');
    setProgress('Creando la Mesa de ayuda');
    try {
      const created = await platformAdminApi.createMesa({
        tenantId,
        companyName: companyName.trim(),
        agentName: agentName.trim() || undefined,
        scope: scope.trim(),
        notifyUserIds: notify,
      });
      setResult(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear la Mesa de ayuda');
    } finally {
      setProgress('');
    }
  };

  if (result) {
    return (
      <RecipeDone
        title={`La Mesa de ayuda de ${companyName} está creada`}
        subtitle='Atiende, abre un ticket por conversación y avisa cuando hace falta una persona. Falta conectarle el WhatsApp.'
        steps={result.steps}
        aiReady={result.aiReady}
        actions={
          <>
            <Button asChild size='lg'>
              <Link to='/admin/whatsapp'>Conectar WhatsApp</Link>
            </Button>
            <Button asChild size='lg' variant='outline'>
              <Link to='/admin/inbox'>Ver la Bandeja</Link>
            </Button>
          </>
        }
      />
    );
  }

  return (
    <RecipePage
      title='Nueva Mesa de ayuda'
      intro='Un agente que atiende los problemas que le cuentan, resuelve lo que puede y llama a una persona cuando no alcanza. Tres respuestas y queda listo.'
    >
      <form onSubmit={handleSubmit} noValidate>
        <ol className='relative'>
          <Question done={answered[0]} title='¿Para qué cliente?' hint='La mesa queda dentro de su cuenta.'>
            <ClientFields
              tenants={tenants}
              tenantId={tenantId}
              onTenant={tenant => {
                setTenantId(tenant.id);
                setCompanyName(tenant.name);
                loadCandidates(tenant.id);
              }}
              companyName={companyName}
              onCompanyName={setCompanyName}
              agentName={agentName}
              onAgentName={setAgentName}
              defaultAgentName={defaultSupportName(companyName)}
            />
          </Question>

          <Question
            done={answered[1]}
            title='¿Qué problemas atiende?'
            hint='Lo que le escribe la gente, con sus palabras. El agente lo usa para saber qué es suyo y qué no.'
          >
            <Label htmlFor='scope' className='sr-only'>
              Problemas que atiende
            </Label>
            <Textarea
              id='scope'
              rows={4}
              maxLength={1500}
              value={scope}
              placeholder={'Cortes de luz y reclamos técnicos.\nInternet lento o sin servicio.\nDudas con la factura.'}
              onChange={event => setScope(event.target.value)}
            />
          </Question>

          <Question
            done={answered[2]}
            last
            title='¿A quién avisa cuando hace falta una persona?'
            hint='Les llega por Telegram a quienes lo tienen cargado. Todos lo ven además en la Bandeja.'
          >
            <NotifyList
              tenantChosen={Boolean(tenantId)}
              candidates={candidates}
              selected={notify}
              onToggle={toggle}
            />
          </Question>
        </ol>

        <RecipeFooter error={error} progress={progress} answered={answered} submitLabel='Crear Mesa de ayuda' />
      </form>
    </RecipePage>
  );
}

function NotifyList(props: {
  tenantChosen: boolean;
  candidates: NotifyCandidate[] | null;
  selected: number[];
  onToggle: (id: number) => void;
}) {
  if (!props.tenantChosen) {
    return <p className='text-sm text-muted-foreground'>Elegí primero el cliente.</p>;
  }
  if (props.candidates === null) {
    return <p className='text-sm text-muted-foreground'>Buscando el equipo del cliente…</p>;
  }
  if (props.candidates.length === 0) {
    return (
      <p className='rounded-lg bg-muted/50 px-4 py-3 text-sm text-foreground'>
        Este cliente todavía no tiene usuarios Admin u Operador. La mesa se crea igual y los tickets quedan en la
        Bandeja; cuando sumes a alguien, lo agregás en la ficha de la mesa.
      </p>
    );
  }
  return (
    <ul className='divide-y divide-border rounded-lg border border-border' aria-label='Equipo del cliente'>
      {props.candidates.map(person => {
        const checked = props.selected.includes(person.id);
        return (
          <li key={person.id}>
            <label
              className={cn(
                'flex cursor-pointer items-center gap-3 px-4 py-3 text-sm',
                !person.canSeeNewBot && 'cursor-not-allowed opacity-60',
              )}
            >
              <input
                type='checkbox'
                className='h-4 w-4 accent-primary'
                checked={checked}
                disabled={!person.canSeeNewBot}
                onChange={() => props.onToggle(person.id)}
              />
              <span className='min-w-0 flex-1 truncate text-foreground'>{person.email}</span>
              <span className='shrink-0 text-muted-foreground'>
                {!person.canSeeNewBot ? 'Solo ve bots elegidos' : person.hasTelegram ? 'Telegram' : 'Solo Bandeja'}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}
